import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Divider,
  Typography,
  Space,
  Button,
  Popconfirm,
  Select,
} from "antd";
import { DeleteOutlined, SaveOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime.js";
import { $t } from "~/i18n.js";
import { MaterialSelect } from "~/components/form/MaterialSelect.jsx";
import { DateRangeInput } from "~/components/form/DateRangeInput.jsx";
import { ReservationStatusButtons } from "./ReservationStatusButtons.jsx";
import { ReservationStatusTag } from "./ReservationStatusTag.jsx";
import { LastUpdated } from "../atom/LastUpdated.jsx";

dayjs.extend(relativeTime);

const { TextArea } = Input;
const { Text, Title } = Typography;

const SAVE = "save";
const DELETE = "delete";

export const ReservationForm = ({
  visible,
  closeModal,
  saveReservation,
  deleteReservation,
  activeReservation,
  isAdmin,
  isOwner,
  vliegers,
  profile,
}) => {
  if (!activeReservation) return null;

  const [form] = Form.useForm();
  const [loadingAction, setLoadingAction] = useState("");
  const isEditing = activeReservation.id !== null;
  const canEdit = !activeReservation.id || isAdmin || isOwner;
  const canDelete = isAdmin || isOwner;

  // if not material loaded on effect, dispatch (d) => dispatch(materiaalActions.getMaterialData(d))
  useEffect(() => {
    if (activeReservation) {
      form.setFieldsValue({
        user_id: activeReservation.user?.id || profile?.id,
        material_id:
          activeReservation.material?.id || activeReservation.material_id,
        date_range: {
          date_from: activeReservation.date_from,
          date_to: activeReservation.date_to,
        },
        user_remark: activeReservation.user_remark || "",
        status: activeReservation.status || "open",
        status_updated_remark: activeReservation.status_updated_remark || "",
      });
    }
  }, [activeReservation, form, profile]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoadingAction(SAVE);

      const reservationData = {
        id: activeReservation.id,
        material_id: values.material_id,
        date_from: values.date_range.date_from,
        date_to: values.date_range.date_to,
        user_remark: values.user_remark || "",
      };

      // Include user_id if admin (for both create and edit)
      if (isAdmin && values.user_id) {
        reservationData.user_id = values.user_id;
      }

      // Only include status fields if admin and editing
      if (isAdmin && isEditing) {
        reservationData.status = values.status;
        reservationData.status_updated_remark =
          values.status_updated_remark || "";
      }

      const result = await saveReservation(reservationData);
      if (result) {
        closeModal();
      }
    } catch (error) {
      console.error("Validation failed:", error);
    } finally {
      setLoadingAction("");
    }
  };

  const handleDelete = async () => {
    setLoadingAction(DELETE);
    try {
      const result = await deleteReservation(activeReservation);
      if (result) {
        closeModal();
      }
    } finally {
      setLoadingAction("");
    }
  };

  const handleCancel = () => {
    form.resetFields();
    closeModal();
  };

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      width={600}
      title={
        <Space>
          {isEditing ? $t("Reservering") : $t("Nieuwe reservering")}
          {isEditing && (
            <ReservationStatusTag status={activeReservation.status} />
          )}
        </Space>
      }
      footer={[
        canDelete && isEditing && (
          <Popconfirm
            key="delete"
            title={$t("Weet je het zeker?")}
            onConfirm={handleDelete}
            okText={$t("Ja")}
            cancelText={$t("Nee")}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={loadingAction === DELETE}
            >
              {$t("Verwijder")}
            </Button>
          </Popconfirm>
        ),
        <Button key="cancel" onClick={handleCancel}>
          {$t("Annuleren")}
        </Button>,
        <Button
          key="save"
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={loadingAction === SAVE}
        >
          {$t("Opslaan")}
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout={"horizontal"}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
        name="reservation"
        preserve={false}
        requiredMark={false}
      >
        {/* User Info - Display only when editing */}
        {isEditing && activeReservation.user && (
          <div style={{ marginBottom: 24 }}>
            <Space>
              <div>
                <Text strong>{activeReservation.user.name}</Text>
                {activeReservation.date_created && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {$t("aangemaakt")}{" "}
                      {dayjs(activeReservation.date_created).fromNow()}
                    </Text>
                  </div>
                )}
              </div>
            </Space>
          </div>
        )}

        {/* User Select - Admin only */}
        {isAdmin && vliegers && (
          <Form.Item
            label={$t("Lid")}
            name="user_id"
            rules={[{ required: true, message: $t("Verplicht") }]}
          >
            <Select
              showSearch
              filterOption={(input, option) =>
                option &&
                option.children &&
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              placeholder={$t("Selecteer lid")}
            >
              {vliegers.map(
                (vlieger, key) =>
                  vlieger.id > 0 && (
                    <Select.Option value={vlieger.id} key={key}>
                      {vlieger.name}
                    </Select.Option>
                  ),
              )}
            </Select>
          </Form.Item>
        )}

        {/* Material Select */}
        <Form.Item
          name="material_id"
          label={$t("Materiaal")}
          rules={[{ required: true, message: $t("Materiaal is verplicht") }]}
        >
          <MaterialSelect disabled={!canEdit} />
        </Form.Item>

        {/* Date Range Input */}
        <Form.Item
          name="date_range"
          label={$t("Periode")}
          rules={[
            { required: true, message: $t("Datum is verplicht") },
            {
              validator: (_, value) => {
                if (!value?.date_from || !value?.date_to) {
                  return Promise.reject($t("Beide datums zijn verplicht"));
                }
                const start = dayjs(value.date_from);
                const end = dayjs(value.date_to);
                if (end.isBefore(start)) {
                  return Promise.reject(
                    $t("Einddatum moet na startdatum zijn"),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <DateRangeInput disabled={!canEdit} />
        </Form.Item>

        {/* User Remark */}
        <Form.Item
          name="user_remark"
          label={$t("Opmerking")}
          rules={[{ max: 100, message: $t("Maximaal 100 tekens") }]}
        >
          <TextArea
            rows={2}
            maxLength={100}
            showCount
            disabled={!canEdit}
            placeholder={$t("Optionele opmerking")}
          />
        </Form.Item>

        {/* Admin Controls - Only show for admins when editing */}
        {isAdmin && isEditing && (
          <>
            <Divider />
            <Title level={5}>{$t("Beheer reservering")}</Title>

            <Form.Item name="status" label={$t("Status")}>
              <ReservationStatusButtons />
            </Form.Item>

            <Form.Item
              name="status_updated_remark"
              label={$t("Status opmerking")}
              rules={[{ max: 100, message: $t("Maximaal 100 tekens") }]}
            >
              <TextArea
                rows={2}
                maxLength={100}
                showCount
                placeholder={$t("Optionele opmerking bij statuswijziging")}
              />
            </Form.Item>
          </>
        )}

        {/* Status Update Info - Display only */}
        <LastUpdated
          date_updated={activeReservation.status_updated_at}
          updated_by={activeReservation.status_updated_by?.name}
        />
      </Form>
    </Modal>
  );
};
